import React from "react";
import { MarketingLayout } from "@/components/MarketingLayout";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export default function WhatIsNpsPage() {
  return (
    <MarketingLayout>
      <section className="max-w-4xl mx-auto px-6 py-16">
        <p className="text-xs uppercase tracking-wider font-semibold text-[#F58220]">Guide</p>
        <h1 className="mt-2 text-4xl md:text-5xl font-extrabold tracking-tight text-[#1F3A6B]">What is Net Promoter Score (NPS)?</h1>
        <p className="mt-5 text-lg text-slate-700 leading-relaxed">
          Net Promoter Score (NPS) is a global benchmark for measuring customer loyalty. Customers answer one simple question:
          <em> "How likely are you to recommend us to a friend or colleague?"</em> on a 0–10 scale. Their answer reveals whether
          they are a <strong>Promoter</strong>, <strong>Passive</strong>, or <strong>Detractor</strong>.
        </p>

        <Card className="mt-8 p-6 border-slate-100 shadow-elev-1">
          <p className="text-sm font-semibold text-[#1F3A6B] uppercase tracking-wider">The formula</p>
          <p className="mt-2 text-2xl font-extrabold text-[#1F3A6B]">NPS = % Promoters − % Detractors</p>
          <p className="mt-2 text-slate-600 text-sm">Score range: −100 to +100. Anything above 0 is good; above 50 is excellent; above 70 is world-class.</p>
        </Card>

        <div className="mt-10 grid md:grid-cols-3 gap-4">
          <Bucket label="Detractors" range="0–6" tone="rose" desc="Unhappy customers who can damage your brand through negative word-of-mouth."/>
          <Bucket label="Passives" range="7–8" tone="amber" desc="Satisfied but unenthusiastic — vulnerable to competitive offerings."/>
          <Bucket label="Promoters" range="9–10" tone="emerald" desc="Loyal enthusiasts who fuel growth through referrals and repeat purchase."/>
        </div>

        <h2 className="mt-12 text-2xl font-extrabold text-[#1F3A6B] tracking-tight">Why NPS works</h2>
        <ul className="mt-3 space-y-2 text-slate-700">
          <li>• <strong>Simple</strong> — one question, universally understood.</li>
          <li>• <strong>Predictive</strong> — strongly correlates with retention & referral.</li>
          <li>• <strong>Comparable</strong> — benchmark against industry & geography.</li>
          <li>• <strong>Actionable</strong> — segments customers into clear loyalty buckets.</li>
        </ul>

        <h2 className="mt-12 text-2xl font-extrabold text-[#1F3A6B] tracking-tight">Beyond the score: closing the loop</h2>
        <p className="mt-3 text-slate-700">A score is only useful if you act on it. CX10X automatically routes detractors to the right team, tracks resolution, and uses AI to surface root causes — so loyalty becomes a system, not a survey.</p>

        <div className="mt-10 flex gap-3">
          <Link to="/calculator"><Button data-testid="whatisnps-calc-btn" size="lg" className="bg-[#F58220] hover:bg-[#E36F0E] text-white">Try NPS Calculator</Button></Link>
          <Link to="/signup"><Button data-testid="whatisnps-signup-btn" size="lg" variant="outline">Start free</Button></Link>
        </div>
      </section>
    </MarketingLayout>
  );
}

function Bucket({ label, range, tone, desc }) {
  const tones = {
    rose: "border-rose-100 bg-rose-50 text-rose-700",
    amber: "border-amber-100 bg-amber-50 text-amber-700",
    emerald: "border-emerald-100 bg-emerald-50 text-emerald-700",
  };
  return (
    <Card className={`p-5 ${tones[tone]} border`}>
      <p className="text-sm font-semibold uppercase tracking-wider opacity-80">{label}</p>
      <p className="mt-1 text-3xl font-extrabold">{range}</p>
      <p className="mt-2 text-sm leading-relaxed text-slate-700">{desc}</p>
    </Card>
  );
}
